import { Vector3 } from '@babylonjs/core';
import { World } from '../core/World';
import { Block } from '../voxel/Block';

export interface BlockHit {
  position: Vector3;      // Block position (integer coordinates)
  normal: Vector3;        // Face normal (direction to place new block)
  blockId: number;        // Type of block hit
  distance: number;       // Distance from ray origin
}

export class BlockPicker {
  private world: World;
  private maxDistance: number;
  private stepSize: number;

  constructor(world: World, maxDistance: number = 8) {
    this.world = world;
    this.maxDistance = maxDistance;
    this.stepSize = 0.1;
  }

  public pick(origin: Vector3, direction: Vector3): BlockHit | null {
    const normalizedDir = direction.normalize();
    let currentPos = origin.clone();
    let lastAirPos = origin.clone();

    for (let distance = 0; distance < this.maxDistance; distance += this.stepSize) {
      currentPos = origin.add(normalizedDir.scale(distance));

      const blockX = Math.floor(currentPos.x);
      const blockY = Math.floor(currentPos.y);
      const blockZ = Math.floor(currentPos.z);

      const blockId = this.world.getBlock(blockX, blockY, blockZ);

      if (Block.isSolid(blockId)) {
        // Calculate which face we hit
        const normal = this.calculateNormal(lastAirPos, currentPos);

        return {
          position: new Vector3(blockX, blockY, blockZ),
          normal: normal,
          blockId: blockId,
          distance: distance,
        };
      }

      lastAirPos = currentPos.clone();
    }

    return null;
  }

  private calculateNormal(from: Vector3, to: Vector3): Vector3 {
    // Determine which face was hit by comparing positions
    const blockTo = new Vector3(
      Math.floor(to.x),
      Math.floor(to.y),
      Math.floor(to.z)
    );

    const blockFrom = new Vector3(
      Math.floor(from.x),
      Math.floor(from.y),
      Math.floor(from.z)
    );

    const diff = blockFrom.subtract(blockTo);

    // Return the dominant axis as normal
    if (Math.abs(diff.x) >= Math.abs(diff.y) && Math.abs(diff.x) >= Math.abs(diff.z)) {
      return new Vector3(Math.sign(diff.x), 0, 0);
    } else if (Math.abs(diff.y) >= Math.abs(diff.x) && Math.abs(diff.y) >= Math.abs(diff.z)) {
      return new Vector3(0, Math.sign(diff.y), 0);
    } else {
      return new Vector3(0, 0, Math.sign(diff.z));
    }
  }

  public getPlacementPosition(hit: BlockHit): Vector3 {
    return hit.position.add(hit.normal);
  }

  public setMaxDistance(distance: number): void {
    this.maxDistance = Math.max(1, Math.min(20, distance));
  }

  public getMaxDistance(): number {
    return this.maxDistance;
  }
}
